import { FC } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Checkbox from '@/storybook/components/FormCollection/Checkbox/Checkbox';
import IconButton from '@/storybook/components/IconButton/IconButton';
import Text from '@/storybook/components/Text/Text';
import TooltipWrapper from '@/components/base/TooltipWrapper/TooltipWrapper';
import PinIcon from '@/storybook/icons/PinIcon';
import { TablePinIcon } from '@/storybook/icons';
import styles from './ColumnConfigItem.module.css';

type VisibilityState = boolean | 'indeterminate';

interface ColumnConfigItemProps {
  id: string;
  label: string;
  isVisible?: boolean;
  checkedState?: VisibilityState;
  isPinned?: boolean;
  isDisabled?: boolean;
  isChild?: boolean;
  isGroup?: boolean;
  canPin?: boolean;
  showCheckbox?: boolean;
  showPinButton?: boolean;
  showPinIcon?: boolean;
  onToggleVisibility?: () => void;
  onTogglePin?: () => void;
  pinTooltip?: string;
}

const ColumnConfigItem: FC<ColumnConfigItemProps> = ({
  id,
  label,
  isVisible = true,
  checkedState,
  isPinned = false,
  isDisabled = false,
  isChild = false,
  isGroup = false,
  canPin = true,
  showCheckbox = false,
  showPinButton = false,
  showPinIcon = false,
  onToggleVisibility,
  onTogglePin,
  pinTooltip,
}) => {
  const { t } = useTranslation();

  return (
    <div
      className={cx(styles.item, {
        [styles.disabled]: isDisabled,
        [styles.childItem]: isChild,
        [styles.groupItem]: isGroup,
      })}
      data-testid={`column-config-item-${id}`}
    >
      {showPinButton && (
        <IconButton
          appearance="text"
          theme="light"
          size="S"
          className={cx(styles.pinButton, { [styles.pinned]: isPinned })}
          onClick={onTogglePin}
          disabled={!isPinned && !canPin}
          title={!isPinned && !canPin ? pinTooltip : undefined}
          aria-label={
            isPinned
              ? t('global.table.columnConfig.unpinColumn', { label })
              : t('global.table.columnConfig.pinColumn', { label })
          }
          data-testid={`column-pin-${id}`}
          icon={<TablePinIcon isPinned={isPinned} />}
        />
      )}

      {showPinIcon && (
        <IconButton
          appearance="text"
          theme="light"
          size="S"
          className={cx(styles.pinButton, styles.pinIconButton)}
          onClick={onTogglePin}
          aria-label={t('global.table.columnConfig.unpinColumn', { label })}
          data-testid={`column-pin-${id}`}
          icon={<PinIcon />}
        />
      )}

      {showCheckbox && (
        <>
          {isDisabled && pinTooltip && <TooltipWrapper id={`pin-limit-${id}`} content={pinTooltip} place="top" />}
          <div
            className={styles.checkboxWrapper}
            {...(isDisabled && pinTooltip ? { 'data-tooltip-id': `pin-limit-${id}` } : {})}
          >
            <Checkbox
              checked={checkedState ?? isVisible}
              disabled={isDisabled}
              onChange={onToggleVisibility}
              size="S"
              data-testid={`column-visibility-${id}`}
            />
          </div>
        </>
      )}

      <Text as="span" className={styles.label}>
        {label}
      </Text>
    </div>
  );
};

export default ColumnConfigItem;
